import { OrderType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/OrderTypeProto';
import { Side } from '@/compiled_proto/com/celertech/positionmanager/api/enums/SideProto';
import { TimeInForceType } from '@/compiled_proto/com/celertech/positionmanager/api/enums/TimeInForceTypeProto';
import InstrumentInfo from '@/components/common/InstrumentInfo';
import {
    TickerOrderFormInput,
    TickerOrderFormValues,
    tickerOrderSchema
} from '@/components/form/schema/tickerOrderSchema';
import { addMarketFull, addMarketTob, switchOrderBook } from '@/services/MarketService';
import { submitLimitOrder, submitMarketOrder } from '@/services/OrderService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType, selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { generatePair } from '@/state/reducers/marketPairSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { setActiveTicker } from '@/state/reducers/tickerSlice';
import { TraderMarket, editWatchListMarket, removeSubscription } from '@/state/reducers/traderMarketSlice';
import { store } from '@/state/store';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useDebouncedValue } from '@/utils/hooks/useDebouncedValue';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { UseDisclosureReturn, useDisclosure } from '@/utils/hooks/useDisclosure';
import { getInstrumentConfig, useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import useMinOrderSize from '@/utils/hooks/useMinOrderSize';
import useTickerOrderBook from '@/utils/hooks/useTickerOrderBook';
import { getAssetName } from '@/utils/symbolMapping';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { yupResolver } from '@hookform/resolvers/yup';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { MdCandlestickChart, MdClose, MdList, MdRefresh } from 'react-icons/md';
import TickerPrice, { Highlights } from './TickerPrice';
import TickerQuantityInput from './TickerQuantityInput';
import TickerSlippageInput from './TickerSlippageInput';

interface TickerWidgetProps {
    id: number;
    market: TraderMarket;
    watchListId: string;
    handlers: UseDisclosureReturn[1];
    onRemove: (id: number) => void;
}

interface TickerProps extends TickerWidgetProps {
    isPopOut?: boolean;
    sortable?: ReturnType<typeof useSortable>;
}

const TickerWidget = (props: TickerWidgetProps) => {
    const { id } = props;

    const sortable = useSortable({ id });
    const { setNodeRef, transform, transition } = sortable;

    const style = {
        transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
        transition
    };

    return (
        <div ref={setNodeRef} className="w-full xsm:w-auto" style={style}>
            <Ticker {...props} sortable={sortable} />
        </div>
    );
};

export default TickerWidget;

const durationOptions: { label: keyof typeof TimeInForceType; value: TimeInForceType }[] = [
    // { label: 'DAY', value: TimeInForceType.DAY },
    { label: 'IOC', value: TimeInForceType.IOC },
    { label: 'FOK', value: TimeInForceType.FOK }
    // { label: 'GTC', value: TimeInForceType.GTC }
];

const orderTypeOptions: { label: string; value: OrderType }[] = [
    { label: 'MKT', value: OrderType.MARKET },
    { label: 'LMT', value: OrderType.LIMIT }
];

const defaultValues: Partial<TickerOrderFormInput> = {
    side: Side.BUY,
    bestPrice: 0,
    quantity: null,
    duration: durationOptions[0],
    orderType: orderTypeOptions[0],
    slippage: '0.00000'
};

const Ticker = (props: TickerProps) => {
    const dispatch = useAppDispatch();
    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const { id, watchListId, market, sortable, handlers, isPopOut = false, onRemove } = props;

    const [baseCcy, quoteCcy] = useMemo(() => market.celer.split('/'), [market]);

    const [activeCcy, setActiveCcy] = useState<string>(baseCcy);
    const showOrderBook = useDisclosure(false);

    const instrument = useInstrument(market.celer);
    const activeCurrencyConfig = useCurrency(activeCcy);

    const { defaultSlippage, ccy2_enabled, price_decimals, config } = instrument;
    const { increments } = activeCurrencyConfig;

    const instrumentType = useMemo(() => {
        switch (config?.type) {
            case 'FX':
                return { label: lang.common.fxSpot, value: 'FX Spot' };
            case 'Crypto':
                return { label: lang.common.cryptoCFD, value: 'Crypto CFD' };
            case 'Index':
                return { label: lang.common.indexCFD, value: 'Index CFD' };
            case 'Commodity':
                return { label: lang.common.commCFD, value: 'Comm CFD' };
            case 'Future':
                return { label: lang.common.futuresCFD, value: 'Futures CFD' };
            default:
                return { label: '', value: '' };
        }
    }, [market.celer, accountType, config, lang]);

    const { orderDecimals, minOrderSize } = useMinOrderSize({ instrument, activeCurrencyConfig });

    const currencyAssetName = useMemo(() => getAssetName(activeCcy), [activeCcy]);
    const formDefaultValues = useMemo(() => {
        const { quantity, orderType, duration } = market.tickerState || {};
        const returnValues = {
            ...defaultValues,
            quantity: quantity || null,
            orderType: orderTypeOptions.find((option) => option.value === orderType) || orderTypeOptions[0],
            duration: durationOptions.find((option) => option.value === duration) || durationOptions[0],
            slippage: defaultSlippage
        };

        return returnValues;
    }, [watchListId, market.tickerState, defaultSlippage]);

    const form = useForm<TickerOrderFormInput>({
        defaultValues: formDefaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            tickerOrderSchema({
                min_order_size: minOrderSize,
                order_decimals: orderDecimals,
                currencyOut: activeCcy,
                currencyPair: market.show
            })
        )
    });

    const { watch, register, getFieldState, getValues, formState } = form;

    const fieldState = getFieldState('quantity', formState);
    const [orderType, quantity] = watch(['orderType', 'quantity']);

    useDidUpdate(() => {
        if (credentials) {
            if (showOrderBook[0]) {
                // console.log('addMarketFull due to open book', market.celer);
                addMarketFull(market.celer);
            } else {
                const marketPair = store.getState().marketPair;
                const activePair = marketPair.activePair;
                // if (activePair?.celer !== market.celer)  addMarketTob(market.celer);
                const noQuantity = !quantity || quantity <= 0;
                // console.log(quantity, noQuantity);
                if (activePair?.celer !== market.celer && noQuantity) {
                    addMarketTob(market.celer, true);
                }
            }
        }
    }, [showOrderBook[0]]);

    const [debouncedQuantity] = useDebouncedValue(quantity, 300);

    useDidUpdate(() => {
        if (credentials) {
            if (debouncedQuantity && debouncedQuantity > 0) {
                // console.log('addMarketFull due to quantity', market.celer);
                addMarketFull(market.celer);
            } else {
                const marketPair = store.getState().marketPair;
                const activePair = marketPair.activePair;
                if (activePair?.celer !== market.celer && !showOrderBook[0]) {
                    // console.log('addMarketTob due to quantity', market.celer);
                    addMarketTob(market.celer);
                }
            }
        }
    }, [debouncedQuantity]);

    return (
        <FormProvider {...form}>
            <div className="relative w-full xsm:w-60 bg-brand-background-dark text-brand-text rounded-sm border border-brand-neutral-700">
                <div className="absolute top-0.5">
                    <InstrumentInfo
                        market={market}
                        offsetOverride={{
                            mainAxis: 2,
                            crossAxis: -1
                        }}
                    />
                </div>
                <div
                    className="ticker-widget-header touch-manipulation flex justify-between items-center bg-brand-ticker hover:bg-brand-ticker-hover cursor-grab active:cursor-grabbing pl-5"
                    {...sortable?.attributes}
                    {...sortable?.listeners}>
                    <div className="flex items-center justify-center">
                        <span className="text-sm font-bold line-clamp-1">
                            {instrumentType.value === 'Futures CFD' ? `CFD/${config.assetName}` : market.show} -{' '}
                            {instrumentType.label}
                        </span>
                    </div>
                    <div className="flex">
                        <div
                            title="Open In Chart"
                            className={cn('h-full flex items-center p-1', {
                                ['cursor-pointer hover:bg-brand-primary']: !isPopOut,
                                ['cursor-not-allowed text-brand-neutral-600']: isPopOut
                            })}
                            onClick={() => {
                                if (!isPopOut) {
                                    const marketPair = store.getState().marketPair;
                                    const activePair = marketPair.activePair;
                                    if (activePair?.celer !== market.celer) switchOrderBook(market, 'trader');
                                }
                            }}>
                            <MdCandlestickChart className="w-4 h-4" name="Open In Chart" />
                        </div>
                        <div
                            title="Close Widget"
                            className={cn('h-full flex items-center p-1', {
                                ['cursor-pointer hover:bg-brand-primary']: !isPopOut,
                                ['cursor-not-allowed text-brand-neutral-600']: isPopOut
                            })}
                            onClick={() => {
                                if (!isPopOut) {
                                    onRemove(id);
                                    dispatch(removeSubscription({ id: watchListId, market }));
                                }
                            }}>
                            <MdClose className="w-4 h-4" name="Close Widget" />
                        </div>
                    </div>
                </div>
                <TickerPrices
                    market={market}
                    config={config}
                    activeCcy={activeCcy}
                    showOrderBook={showOrderBook}
                    priceDecimals={price_decimals}
                />
                <div className="p-1">
                    <div className="flex">
                        <TickerQuantityInput
                            {...register('quantity')}
                            min={minOrderSize}
                            step={increments}
                            active_ccy={activeCcy}
                            instrumentType={instrumentType.value as any}
                            currencyAssetName={currencyAssetName}
                            ccy2_enabled={ccy2_enabled}
                            switchCcy={() => setActiveCcy(activeCcy === baseCcy ? quoteCcy : baseCcy)}
                            onChangeCallback={(value) => {
                                const [orderType, duration] = getValues(['orderType', 'duration']);
                                const payload = {
                                    market: {
                                        ...market,
                                        tickerState: {
                                            quantity: value,
                                            orderType: orderType?.value || OrderType.MARKET,
                                            duration: duration?.value || TimeInForceType.IOC,
                                            orderBook: market.tickerState?.orderBook
                                        }
                                    }
                                };
                                dispatch(editWatchListMarket(payload));
                            }}
                        />
                    </div>
                    {fieldState.error ? (
                        <span className="text-brand-red italic text-xs">{fieldState.error?.message}</span>
                    ) : null}
                    <div className="flex">
                        <div
                            className="flex justify-center items-center px-1.5 border border-brand-neutral-700 cursor-pointer hover:bg-brand-primary"
                            onClick={() => {
                                const [quantity, orderType, duration] = getValues([
                                    'quantity',
                                    'orderType',
                                    'duration'
                                ]);
                                const payload = {
                                    market: {
                                        ...market,
                                        tickerState: {
                                            quantity: quantity || null,
                                            orderType: orderType?.value || OrderType.MARKET,
                                            duration: duration?.value || TimeInForceType.IOC,
                                            orderBook: !showOrderBook[0] ? ('opened' as const) : ('closed' as const)
                                        }
                                    }
                                };
                                dispatch(editWatchListMarket(payload));
                                showOrderBook[1].toggle();
                            }}>
                            <MdList className="w-5 h-5" />
                        </div>
                        <TickerOrderTypeSwitch market={market} />
                        <DurationSwitch market={market} />
                        {/* <DurationSwitch options={durationOptions} /> */}
                        <TickerSlippageInput
                            {...register('slippage')}
                            disabled={orderType?.value === OrderType.MARKET}
                            defaultSlippage={defaultSlippage}
                            step={0.1}
                        />
                        <div
                            className="flex items-center justify-center py-1 px-2 text-sm border border-brand-neutral-700 hover:bg-brand-primary cursor-pointer whitespace-nowrap"
                            onClick={() => {
                                dispatch(setActiveTicker(generatePair(market.celer)));
                                handlers.toggle();
                            }}>
                            {lang.tickers.order}
                        </div>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

const TickerPrices = memo(
    ({
        market,
        config,
        activeCcy,
        showOrderBook,
        priceDecimals
    }: {
        market: TraderMarket;
        config: ReturnType<typeof getInstrumentConfig>;
        activeCcy: string;
        showOrderBook: UseDisclosureReturn;
        priceDecimals?: number;
    }) => {
        const credentials = useAppSelector(selectCredentials);
        const currentAccount = useAppSelector(selectCurrentAccount);

        const [baseCcy, quoteCcy] = useMemo(() => market.celer.split('/'), [market]);

        const { pip_size } = useInstrument(market.celer);

        const isCcy2Order = useMemo(() => activeCcy === quoteCcy, [activeCcy, quoteCcy]);
        const currencyAssetName = useMemo(() => getAssetName(activeCcy), [activeCcy]);

        const form = useFormContext<TickerOrderFormInput>();

        const { watch, handleSubmit } = form;

        const [quantity, duration] = watch(['quantity', 'duration']);

        const { bestBid, bestAsk, bidVWAP, askVWAP, spread, vwapSpread, settlementDate } = useTickerOrderBook(
            market,
            quantity || 0,
            duration?.value || TimeInForceType.IOC,
            priceDecimals
        );

        const onSubmit = useCallback(
            async (data: TickerOrderFormInput, e) => {
                const dataValidated = data as TickerOrderFormValues;
                if (dataValidated.quantity && credentials) {
                    if (data.orderType?.value === OrderType.MARKET) {
                        await submitMarketOrder(
                            {
                                spotPrice: data.bestPrice as number,
                                securityId: market.celer,
                                currencyOut: activeCcy,
                                quantity: dataValidated.quantity,
                                timeInForce: dataValidated.duration.value as TimeInForceType,
                                side: dataValidated.side as Side
                            },
                            credentials,
                            settlementDate,
                            currentAccount
                        );
                    } else if (data.orderType?.value === OrderType.LIMIT) {
                        const slippage = dataValidated.slippage
                            ? BigNumber(dataValidated.slippage)
                                  .dividedBy(Math.pow(10, pip_size || 0))
                                  .toString()
                            : '0';
                        if (dataValidated.quantity && credentials) {
                            await submitLimitOrder(
                                {
                                    securityId: market.celer,
                                    currencyOut: activeCcy,
                                    quantity: dataValidated.quantity,
                                    limitPrice: dataValidated.bestPrice,
                                    timeInForce: dataValidated.duration.value as TimeInForceType,
                                    side: dataValidated.side as Side,
                                    slippage
                                },
                                credentials,
                                settlementDate,
                                currentAccount
                            );
                        }
                    }
                }
            },
            [activeCcy, currentAccount, credentials, market.celer, pip_size, settlementDate]
        );

        const onError = (errors) => console.log(errors);

        return (
            <div
                className={cn('relative flex p-1 gap-1 text-sm border-t border-brand-neutral-700', {
                    'border-b': !showOrderBook[0],
                    'pb-0': showOrderBook[0]
                })}>
                <TickerPrice
                    market={market}
                    config={config}
                    showOrderBook={showOrderBook}
                    currencyAssetName={currencyAssetName}
                    side={isCcy2Order ? Side.BUY : Side.SELL}
                    bestPrice={bidVWAP.price || bestBid.price}
                    onSubmit={handleSubmit(onSubmit, onError)}
                    alignItems="left"
                    priceType="bid"
                    isCcy2Order
                    disabled={bidVWAP.isNoPrice}>
                    <Highlights
                        ticker={bidVWAP.pricePips || bestBid.pricePips}
                        highlights={bidVWAP.highlights || bestBid.highlights}
                        isNoPrice={bidVWAP.isNoPrice}
                    />
                </TickerPrice>
                <div className="absolute w-full h-14 flex justify-center items-center top-0 left-0">
                    <span className="absolute text-xs border border-brand-neutral-700 bg-brand-background-dark p-1 px-2 rounded-full z-[2]">
                        {vwapSpread || spread || '-'}
                    </span>
                </div>
                <TickerPrice
                    market={market}
                    config={config}
                    showOrderBook={showOrderBook}
                    currencyAssetName={currencyAssetName}
                    bestPrice={askVWAP.price || bestAsk.price}
                    side={isCcy2Order ? Side.SELL : Side.BUY}
                    onSubmit={handleSubmit(onSubmit, onError)}
                    alignItems="right"
                    priceType="ask"
                    isCcy2Order
                    disabled={askVWAP.isNoPrice}>
                    <Highlights
                        ticker={askVWAP.pricePips || bestAsk.pricePips}
                        highlights={askVWAP.highlights || bestAsk.highlights}
                        isNoPrice={askVWAP.isNoPrice}
                    />
                </TickerPrice>
            </div>
        );
    }
);

TickerPrices.displayName = 'TickerPrices';

const TickerOrderTypeSwitch = ({ market }: { market: TraderMarket }) => {
    const dispatch = useAppDispatch();
    const { watch, setValue, getValues } = useFormContext<TickerOrderFormInput>();

    const orderType = watch('orderType');
    return (
        <div
            className="flex-1 min-w-[46px] px-2 text-sm flex items-center justify-between md:justify-center cursor-pointer border border-brand-neutral-700 hover:bg-brand-primary"
            onClick={() => {
                const [quantity, orderType, duration] = getValues(['quantity', 'orderType', 'duration']);
                const payload = {
                    market: {
                        ...market,
                        tickerState: {
                            quantity: quantity || null,
                            orderType: orderType?.value || OrderType.MARKET,
                            duration: duration?.value || TimeInForceType.IOC,
                            orderBook: market.tickerState?.orderBook
                        }
                    }
                };

                if (orderType?.value === OrderType.MARKET) {
                    payload.market.tickerState.orderType = OrderType.LIMIT;
                    setValue('orderType', orderTypeOptions[1]);
                } else {
                    payload.market.tickerState.orderType = OrderType.MARKET;
                    setValue('orderType', orderTypeOptions[0]);
                }
                dispatch(editWatchListMarket(payload));
            }}>
            {orderType?.label}
            <MdRefresh className="block md:hidden" />
        </div>
    );
};

const DurationSwitch = ({ market }: { market: TraderMarket }) => {
    const dispatch = useAppDispatch();
    const { watch, setValue, getValues } = useFormContext<TickerOrderFormInput>();

    const duration = watch('duration');
    return (
        <div
            className="flex-1 min-w-[46px] px-2 text-sm flex items-center justify-between md:justify-center cursor-pointer border border-brand-neutral-700 hover:bg-brand-primary"
            onClick={() => {
                const [quantity, orderType, duration] = getValues(['quantity', 'orderType', 'duration']);
                const payload = {
                    market: {
                        ...market,
                        tickerState: {
                            quantity: quantity || null,
                            orderType: orderType?.value || OrderType.MARKET,
                            duration: duration?.value || TimeInForceType.IOC,
                            orderBook: market.tickerState?.orderBook
                        }
                    }
                };

                if (duration?.value === TimeInForceType.IOC) {
                    payload.market.tickerState.duration = TimeInForceType.FOK;
                    setValue('duration', durationOptions[1]);
                } else {
                    payload.market.tickerState.duration = TimeInForceType.IOC;
                    setValue('duration', durationOptions[0]);
                }
                dispatch(editWatchListMarket(payload));
            }}>
            {TimeInForceType[duration?.value as number]}
            <MdRefresh className="block md:hidden" />
        </div>
    );
};
